import { styled } from "@mui/material";
import { ZipCodeResponse } from "entities/UIModel";
import React, {
  useState,
} from "react";
import { useWatch } from "react-hook-form";
import theme from "theme/default";
import { gaps } from "theme/defaultStyle";
import { useEsb } from "utils/context/EsbContext";
// import { usePricingActions } from "utils/context/PricingContext";
import useFormWrapper, {
  getNameString,
} from "utils/custom-hooks/useFormWrapper";
import { FullAddressProps } from "utils/interfaces/BaseAddress";
import AddressCheckboxes from "./AddressCheckboxes";
import AddressOneField from "./AddressOneField";
import AddressTwoField from "./AddressTwoField";
import CityField from "./CityField";
// import CountyField from "./CountyField";
import StateField from "./StateField";
import ZipCodeField from "./ZipCodeField";

const Container = styled("div")({
  display: "flex",
  flexDirection: "row",
  // flexWrap: "nowrap",
  gap:gaps.large1,
  // padding :"4px",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    width: "100%",
    gap:gaps.large1,
  },
  
  "& > *": {
    flexBasis: "content",
    flexGrow: 1,
    flexShrink: 1,
  },
  "& > .address-one": {
    minWidth: "40%",
    maxWidth: "40%",
  },
  "& > .address-two": {
    maxWidth: "20%",
    minWidth: "15%",
  },
  "& > .city": {
    maxWidth: "20%",
    minWidth: "15%",
  },
  "& > .state": {
    maxWidth: "9%",
    minWidth: "9%",
  },
  "& > .zip-code": {
    maxWidth: "30%",
    minWidth: "10%",
  },
  "& > .address, & > .city, & > .state, & > .zip-code, & > .county": {
    [theme.breakpoints.down("md")]: {
      maxWidth: "100%",
    },
  },
});

const FullAddress = ({
  schema,
  isPropertySection = true,
  showPropertyCheckbox = false,
  isDisabledAddress = false,
  isStatesDisabled = false,
  addressOneLabelPrefix = "",
  addressTwoLabelPrefix = "",
  cityLabelPrefix = "",
  stateLabelPrefix = "",
  zipCodeLabelPrefix = "",
  addressOneLabelSuffix = "",
  addressTwoLabelSuffix = "",
  cityLabelSuffix = "",
  stateLabelSuffix = "",
  zipCodeLabelSuffix = "",
  callbackIndex,
  showForeignAddressCheckbox = false,
  onForeignAddressChange,
  onBlur,
  setNotDefault,
  verifyAddressHandler,
  onZipcodeUpdate,
}: FullAddressProps) => {
  const { register, getValues, setValue } = useFormWrapper();
  const [, { getZipCode }] = useEsb();

  const stateFieldName = getNameString(`${schema}.state`);
  const cityFieldName = getNameString(`${schema}.city`);
  const zipCodeFieldName = getNameString(`${schema}.zipCode`);
  const addressOneFieldName = getNameString(`${schema}.addressOne`);
  const addressTwoFieldName = getNameString(`${schema}.addressTwo`);
  // const countyFieldName = getNameString(`${schema}.county`);
  const taxCodeFieldName = getNameString(`${schema}.taxCode`);
  const isForeignAddressName = getNameString(`${schema}.isForeignAddress`);

  const [isForeignAddress] = useWatch({
    name: [isForeignAddressName],
  });

  const [currentZipcodeResults, setCurrentZipcodeResults] = useState<ZipCodeResponse>();

  const resetZipcodeData = () => {
    setCurrentZipcodeResults(undefined);
    onZipcodeUpdate?.(currentZipcodeResults)
  };

  const getZipcodeData = async (zipCode: string, invokedExplicitly: boolean = false, selectedState: string = "") => {
    if (isPropertySection && invokedExplicitly) {
      setValue(`${schema}.county`, { code: "", name: "" });
    }

    const currentPropertyState = getValues(stateFieldName);
    if(currentPropertyState?.abbr) {
      selectedState = currentPropertyState.abbr;
    }
    const zipCodeResult = await getZipCode(zipCode, selectedState);    
    if (!zipCodeResult) return;    
    setCurrentZipcodeResults(zipCodeResult);
    onZipcodeUpdate?.(zipCodeResult)
  };

  const handleAutocompleteSelected = async () => {
    const [zipCode,statevalue] = getValues([zipCodeFieldName, stateFieldName]);    
    await getZipcodeData(zipCode, false, statevalue?.abbr);
  };

  const handleZipCodeChange = (value: string) => {
    setNotDefault && setNotDefault();
  };

  // This function to enable the calculate button for only TN when county field value change


  return (
    <>
      {(showPropertyCheckbox || showForeignAddressCheckbox) &&
        <AddressCheckboxes
          schema={schema}
          showPropertyCheckbox={showPropertyCheckbox}
          showForeignAddressCheckbox={showForeignAddressCheckbox}
          callbackIndex={callbackIndex}
          isDisabledAddress={isDisabledAddress}
          onForeignAddressChange={onForeignAddressChange}
          onBlurHandler={onBlur}
        />
      }
      {!isForeignAddress && (<>
        <Container>
          <div className="address address-one">
            <AddressOneField
              setNotDefault={setNotDefault}
              onBlur={onBlur}
              name={addressOneFieldName}
              schema={schema}
              labelPrefix={addressOneLabelPrefix}
              isDisabled={isDisabledAddress}
              isPropertySection={isPropertySection}
              addressTwoFieldName={addressTwoFieldName}
              stateFieldName={stateFieldName}
              cityFieldName={cityFieldName}
              zipCodeFieldName={zipCodeFieldName}
              onAutocompleteSelected={handleAutocompleteSelected}
              verifyAddressHandler={verifyAddressHandler}
            />
          </div>
          <div className="address address-two">
            <AddressTwoField
              name={addressTwoFieldName}
              label={`${addressTwoLabelPrefix} Address Line 2`}
              labelPrefix={addressTwoLabelPrefix}
              labelSuffix={addressTwoLabelSuffix}
              isDisabled={isDisabledAddress}
              onBlur={onBlur}
              onChange={setNotDefault}
            />
          </div>
          <div className="city">
            <CityField
              name={cityFieldName}
              disabled={isDisabledAddress}
              onChange={setNotDefault}
              zipCodeResults={currentZipcodeResults}
              onBlur={onBlur}
            />
          </div>
          <div className="state">
            <StateField
              name={stateFieldName}
              labelPrefix={stateLabelPrefix}
              isDisabled={isDisabledAddress || isStatesDisabled}
              isPropertySection={isPropertySection}
              autosetValue={false}
              zipCodeResults={currentZipcodeResults}
              resetZipcodeData={resetZipcodeData}
              setNoDefault={setNotDefault}
              onBlur={onBlur}
            />
          </div>
          <div className="zip-code">
            <ZipCodeField
              name={zipCodeFieldName}
              labelPrefix={zipCodeLabelPrefix}
              isDisabled={isDisabledAddress}
              isPropertySection={isPropertySection}
              getZipcodeData={getZipcodeData}
              onChange={handleZipCodeChange}
              onBlur={onBlur}
            />
          </div>

      
          <input type="hidden" {...register(`${taxCodeFieldName}` as const)} />
        </Container>
        {/* {isPropertySection && (
            <div className="county">
              <CountyField
                name={countyFieldName}
                labelPrefix={cityLabelPrefix}
                isDisabled={isDisabledAddress}
                isPropertySection={isPropertySection}
                onChange={enbaleCalculateButtonCheck}
                zipCodeResults={currentZipcodeResults}
              />
            </div>
          )} */}
        </>
      )}
    </>
  );
};

export default FullAddress;
/* eslint-disable @typescript-eslint/no-unused-vars */
